import { Box, Loader, Stack, Text, Title } from '@mantine/core';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { AuthShell } from '../components/auth-shell/AuthShell';
import { useAuth } from '../lib/auth-context';

function SignOutScreen() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  // Sign out exactly once per visit, even if `signOut`/`navigate` change
  // identity while the request is in flight (they re-run this effect).
  // `mounted` (not a per-run cancelled flag) gates the redirect, so a re-run
  // that returns early — or StrictMode's dev remount — can't strand the user
  // on this screen.
  const started = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void (async () => {
      try {
        await signOut();
      } finally {
        if (mounted.current) {
          void navigate({ to: '/' });
        }
      }
    })();
  }, [signOut, navigate]);

  return (
    <AuthShell>
      <Stack gap="lg" align="center">
        <Box>
          <Text
            size="xs"
            tt="uppercase"
            c="brand.7"
            fw={600}
            ta="center"
            style={{ letterSpacing: '0.1em' }}
          >
            See you on the road
          </Text>
          <Title
            order={1}
            mt={6}
            ta="center"
            style={{
              fontSize: 'clamp(1.4rem, 3vw, 1.8rem)',
              lineHeight: 1.2,
              letterSpacing: '-0.015em',
            }}
          >
            Signing you out.
          </Title>
        </Box>
        <Loader color="brand.6" />
        <Text c="ink.6" ta="center" style={{ lineHeight: 1.55 }}>
          One moment.
        </Text>
      </Stack>
    </AuthShell>
  );
}

export const Route = createFileRoute('/sign-out')({
  component: SignOutScreen,
});
